import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Download, Lock } from 'lucide-react';
import ContextMenu from '../ui/ContextMenu';
import { useAuthStore } from '../../store/authStore';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import { isLimitedPlan } from '../../services/personalSyncConsent';
import { chatRowMenuItems } from '../ChatRowContextMenu';
import { setChannelMuted } from '../../services/chatRowActions';
import { isChannelMuted } from '../../utils/chatAlerts';
import ChatSearchBar from './ChatSearchBar';
import ChatExportDialog from './ChatExportDialog';
import './ChatSearch.css';

/**
 * Right-hand group of the conversation header: the search field and the "…"
 * menu (Export chat history, Mute/Unmute).
 *
 * Export is paid (Pro, Team, Enterprise). The badge shows only when the plan
 * is KNOWN to be free — `user` is null during cold start, and a Team account
 * must not be told export is paid until /auth/me lands (the ChatSidebar
 * precedent, D15). The server's 402 is the real gate either way.
 */
export default function ChatHeaderActions({ channel, channelName, currentUserId }) {
  const { t } = useTranslation();
  const planUser = useAuthStore((s) => s.user);
  const exportLocked = Boolean(planUser?.plan) && isLimitedPlan(planUser);
  const [menu, setMenu] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const onExport = () => {
    if (exportLocked) {
      setUpgradeReason(t('chat.export.upgradeReason'));
      setUpgradeModalOpen(true);
      return;
    }
    setExportOpen(true);
  };

  const mute = chatRowMenuItems(channel, t, currentUserId).find((i) => i.id === 'mute');
  const items = [
    {
      icon: <Download size={14} />,
      label: (
        <span className="chat-menu-export">
          {t('chat.header.exportHistory')}
          {exportLocked && (
            <>
              <span className="chat-pro-badge">Pro</span>
              <Lock size={11} className="chat-pro-lock" aria-label={t('chat.export.proLocked')} />
            </>
          )}
        </span>
      ),
      onClick: onExport,
    },
    mute && {
      icon: <mute.Icon size={14} />,
      label: mute.label,
      onClick: () => setChannelMuted(channel.id, !isChannelMuted(channel)),
    },
  ];

  return (
    <div className="chat-header-actions">
      <ChatSearchBar channel={channel} />
      {/* No tooltip: "…" explains itself, and the tooltip sat on top of the
          menu it opened (owner, 30.09.2026). The label stays for screen readers. */}
      <button
        type="button"
        className="chat-icon-btn"
        aria-label={t('chat.header.more')}
        aria-haspopup="menu"
        aria-expanded={Boolean(menu)}
        onClick={(e) => {
          // Anchored to the button, not the pointer (CalendarToolbar precedent:
          // keyboard-opened clicks carry 0,0): right edge under the button's
          // right edge, clamped inside the window by ContextMenu.
          const r = e.currentTarget.getBoundingClientRect();
          setMenu({ x: r.right, y: r.bottom + 4 });
        }}
      >
        <MoreHorizontal size={16} />
      </button>
      {menu && <ContextMenu x={menu.x} y={menu.y} align="end" items={items} onClose={() => setMenu(null)} />}
      <ChatExportDialog
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        channel={channel}
        channelName={channelName}
      />
    </div>
  );
}
